/**
 * ExampleDetailsView — «what's inside» for a library example BEFORE inserting
 * (approved mocks: block-library-example-details/*). One shared content view:
 * - ExampleDetailsDialog wraps it in a BaseDialog (palette ⓘ Details);
 * - BlockLibraryBrowserDialog embeds it as an in-dialog content swap (FP-4:
 *   never a second modal on top of the browser).
 *
 * Cases: normal (real V2ScreenView filmstrip + enlarge), broken envelope
 * (details still open, screens replaced by the renderability reason, Add
 * disabled), long instructions (dialog body scrolls), contract fit vs the
 * CURRENT strategy. Add NEVER inserts by itself — it routes to the existing
 * insert flow (model chosen at the launch surface, developer.md rule 21).
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BaseDialog } from '@modules/ui-components/dialogs/BaseDialog';
import { DialogHeader } from '@modules/ui-components/dialogs/DialogHeader';
import { DialogContent } from '@modules/ui-components/dialogs/DialogContent';
import { DialogFooter } from '@modules/ui-components/dialogs/DialogFooter';
import {
  assessLibraryExampleRenderability,
  computeContractFit,
  type BlockLibraryExampleDoc,
} from '@modules/shared-types';
import { V2ScreenView, AutoScaleScreenContainer } from '@modules/screen-view-v2';
import { CANVAS_NODE_DIMENSIONS } from '@modules/reactFlow/constants/screenPreviewConfig';
import type { StepNode } from '@segmently/schema-models/v2';
import { ExampleScreensFilmstrip } from './ExampleScreensFilmstrip';
import { exampleInstructions, examplePreviewScreens, exampleRationale } from '../utils/exampleEnvelope';
import { fitLine } from '../utils/contractFitText';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

const LEVEL_COLORS: Record<string, string> = {
  mustHave: 'error.main',
  should: 'warning.main',
  forbidden: 'secondary.main',
};

export interface ExampleDetailsViewProps {
  example: BlockLibraryExampleDoc;
  source: 'project' | 'system';
  /** Current strategy variables — renders the contract-fit section when set. */
  strategyVariables?: Record<string, unknown>;
  strategyName?: string;
}

export const ExampleDetailsView: React.FC<ExampleDetailsViewProps> = ({
  example,
  source,
  strategyVariables,
  strategyName,
}) => {
  const [enlargedIndex, setEnlargedIndex] = useState<number | null>(null);

  const renderability = useMemo(() => assessLibraryExampleRenderability(example), [example]);
  const screens = useMemo(() => examplePreviewScreens(example), [example]);
  const rationale = useMemo(() => exampleRationale(example), [example]);
  const instructions = useMemo(() => exampleInstructions(example), [example]);
  const fit = useMemo(
    () => (strategyVariables ? computeContractFit(example, strategyVariables) : []),
    [example, strategyVariables],
  );
  const blueprintSummary = useMemo(() => {
    if (!renderability.renderable || !example.blueprint?.dataSemantics) {
      return { evidenceReferences: 0, assetIntents: 0, assetExamples: 0 };
    }
    const evidenceRefs = new Set<string>();
    for (const item of example.blueprint.dataSemantics.blockDataUsage?.items ?? []) {
      if (typeof item.sourcePath === 'string' && item.sourcePath.trim()) evidenceRefs.add(item.sourcePath);
    }
    let assetIntents = 0;
    for (const screen of example.blueprint.screens) {
      for (const item of screen.aiMeta?.dataMapping ?? []) {
        if (typeof item.sourcePath === 'string' && item.sourcePath.trim()) evidenceRefs.add(item.sourcePath);
      }
      assetIntents += screen.aiMeta?.assetIntents?.length ?? 0;
    }
    return {
      evidenceReferences: evidenceRefs.size,
      assetIntents,
      assetExamples: example.blueprint.assetExamples?.length ?? 0,
    };
  }, [example, renderability.renderable]);

  const enlarged = enlargedIndex !== null ? screens[enlargedIndex] : undefined;

  return (
    <Stack spacing={1.5} data-testid={STRATEGY_CANVAS.exampleDetailsView}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" fontWeight={600}>{example.name}</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={source === 'system' || example.systemExampleId ? 'primary' : 'default'}
          label={source === 'system' || example.systemExampleId ? 'System' : 'My'}
        />
        <Chip size="small" color="primary" label="Blueprint example" />
        {(example.tags ?? []).map((tag) => (
          <Chip key={tag} size="small" variant="outlined" label={tag} />
        ))}
        <Chip size="small" variant="outlined" label={`${example.screensCount} screens`} />
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        data-testid={STRATEGY_CANVAS.exampleDetailsBlueprintSummary}
      >
        <Chip size="small" variant="outlined" label={`${blueprintSummary.evidenceReferences} evidence refs`} />
        <Chip size="small" variant="outlined" label={`${blueprintSummary.assetIntents} asset intents`} />
        <Chip size="small" variant="outlined" label={`${blueprintSummary.assetExamples} asset examples`} />
      </Stack>

      {rationale && (
        <div>
          <Typography variant="overline" color="text.secondary">Rationale</Typography>
          <Typography variant="body2">{rationale}</Typography>
        </div>
      )}

      {instructions.length > 0 && (
        <div>
          <Typography variant="overline" color="text.secondary">Instructions</Typography>
          <Stack spacing={0.5}>
            {instructions.map((entry, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{
                  width: 3, alignSelf: 'stretch', borderRadius: 1, flexShrink: 0,
                  bgcolor: LEVEL_COLORS[entry.level] ?? 'divider',
                }}
                />
                <Typography variant="body2" sx={{ fontSize: 12.5 }}>
                  <b>{entry.level.replace(/([A-Z])/g, ' $1').toUpperCase()}</b> · {entry.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </div>
      )}

      {fit.length > 0 && (
        <div>
          <Typography variant="overline" color="text.secondary">
            Contract{strategyName ? ` · fit with «${strategyName}»` : ''}
          </Typography>
          <Stack spacing={0.25} data-testid={STRATEGY_CANVAS.exampleDetailsFit}>
            {fit.map((entry) => (
              <Typography key={`${entry.kind}-${entry.exampleVarId}`} variant="caption" component="div">
                {fitLine(entry)}
              </Typography>
            ))}
          </Stack>
        </div>
      )}

      {!renderability.renderable ? (
        <Alert severity="warning" data-testid={STRATEGY_CANVAS.exampleDetailsBroken}>
          Screens cannot be previewed or inserted: {renderability.reason}
        </Alert>
      ) : enlarged ? (
        <div>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <IconButton
              size="small"
              aria-label="Previous screen"
              disabled={enlargedIndex === 0}
              onClick={() => setEnlargedIndex((index) => Math.max(0, (index ?? 0) - 1))}
            >
              <ChevronLeftIcon />
            </IconButton>
            {(() => {
              // Canvas-true proportions (430×932 or the screen's own
              // containerSize) — same rule as the filmstrip thumbnails.
              const container = (enlarged.containerSize ?? {}) as { width?: number; height?: number };
              const content = typeof container.width === 'number' && typeof container.height === 'number'
                && container.width > 0 && container.height > 0
                ? { width: container.width, height: container.height }
                : { width: CANVAS_NODE_DIMENSIONS.WIDTH, height: CANVAS_NODE_DIMENSIONS.HEIGHT };
              const boxHeight = 498;
              const boxWidth = Math.min(
                Math.round((boxHeight * content.width) / content.height),
                Math.round(boxHeight * 0.75),
              );
              return (
                <Box
                  data-testid={STRATEGY_CANVAS.exampleDetailsEnlarged}
                  sx={{
                    width: boxWidth, height: boxHeight, border: 1, borderColor: 'divider', borderRadius: 3,
                    overflow: 'hidden', bgcolor: 'background.default',
                  }}
                >
                  <AutoScaleScreenContainer
                    scaleOptions={{ contentWidth: content.width, contentHeight: content.height, padding: 0 }}
                    showBeforeReady
                  >
                    <div style={{ width: content.width, height: content.height, pointerEvents: 'none' }}>
                      <V2ScreenView step={enlarged as unknown as StepNode} locale="en-US" previewMode="static" />
                    </div>
                  </AutoScaleScreenContainer>
                </Box>
              );
            })()}
            <IconButton
              size="small"
              aria-label="Next screen"
              disabled={enlargedIndex === screens.length - 1}
              onClick={() => setEnlargedIndex((index) => Math.min(screens.length - 1, (index ?? 0) + 1))}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
          <Typography variant="caption" component="div" sx={{ textAlign: 'center', mt: 0.5 }}>
            {String(enlarged.name ?? `Screen ${(enlargedIndex ?? 0) + 1}`)} · {(enlargedIndex ?? 0) + 1} of {screens.length}
            {' · '}
            <Box
              component="span"
              role="button"
              tabIndex={0}
              data-testid={STRATEGY_CANVAS.exampleDetailsBackToStrip}
              onClick={() => setEnlargedIndex(null)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setEnlargedIndex(null);
                }
              }}
              sx={{ color: 'primary.main', cursor: 'pointer' }}
            >
              back to all screens
            </Box>
          </Typography>
        </div>
      ) : (
        <div>
          <Typography variant="overline" color="text.secondary">
            Screens ({screens.length})
          </Typography>
          <ExampleScreensFilmstrip
            screens={screens}
            size="medium"
            onScreenClick={setEnlargedIndex}
            data-testid={STRATEGY_CANVAS.exampleDetailsScreens}
          />
          <Typography variant="caption" color="text.secondary">
            Click a screen to enlarge.
          </Typography>
        </div>
      )}
    </Stack>
  );
};

ExampleDetailsView.displayName = 'ExampleDetailsView';

export interface ExampleDetailsDialogProps extends ExampleDetailsViewProps {
  open: boolean;
  onClose: () => void;
  /** Routes to the existing insert flow (Insert dialog with model choice). */
  onAddToCanvas?: () => void;
  /** Explicit pre-placement secondary path. */
  onAdaptImmediately?: () => void;
}

export const ExampleDetailsDialog: React.FC<ExampleDetailsDialogProps> = ({
  open,
  onClose,
  onAddToCanvas,
  onAdaptImmediately,
  ...viewProps
}) => {
  const renderability = assessLibraryExampleRenderability(viewProps.example);
  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      mode="modal"
      data-testid={STRATEGY_CANVAS.exampleDetailsDialog}
      slotProps={{ paper: { 'aria-label': 'Example details' } }}
    >
      <DialogHeader title="Example details" onClose={onClose} />
      {/* Grow with the viewport — the DialogContent default is a FIXED 500px
          scroller that clips the filmstrip at the fold (designer invariant 5). */}
      <DialogContent maxHeight="calc(100vh - 240px)">
        <ExampleDetailsView {...viewProps} />
      </DialogContent>
      <DialogFooter
        primaryAction={{
          label: 'Add draft to canvas',
          onClick: () => onAddToCanvas?.(),
          disabled: !onAddToCanvas || !renderability.renderable || viewProps.example.enabled === false,
          'data-testid': STRATEGY_CANVAS.exampleDetailsAdd,
        } as never}
        secondaryAction={{
          label: 'Adapt immediately…',
          variant: 'text',
          onClick: () => onAdaptImmediately?.(),
          disabled: !onAdaptImmediately || !renderability.renderable || viewProps.example.enabled === false,
          'data-testid': STRATEGY_CANVAS.exampleDetailsImmediate,
        } as never}
      />
    </BaseDialog>
  );
};

ExampleDetailsDialog.displayName = 'ExampleDetailsDialog';
